/*
  InvoiceDocument.jsx
  ────────────────────
  A4 GST-compliant Invoice modelled on the Gujarat Freight Tools reference:
    [ LEFT: Company block          |  RIGHT: TAX INVOICE title + meta table ]
    [ Customer Detail (Bill-To)                                             ]
    [ Line-items table with CGST/SGST OR IGST breakdown per row             ]
    [ LEFT: Bank Details + Total in Words  |  RIGHT: Totals stack           ]
    [ LEFT: Terms & Conditions             |  RIGHT: Signature / Stamp      ]
    [ Footer                                                                ]

  Works for both Proforma and Tax invoices — the title, meta rows and status
  banner switch based on `invoice.doc_type` / `invoice.status`.
*/
import React from "react";
import { QRCodeSVG } from "qrcode.react";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
};

const STAMP_URL = "/branding/stamp.png";
const SIGN_URL = "/branding/signature.png";
const LOGO_URL_LOCAL = "/branding/logo.png";

const BRAND = "#0066CC";
const BRAND_DARK = "#004A99";
const BORDER = "#94a3b8";
const BORDER_SOFT = "#cbd5e1";
const HEAD_BG = "#e8f1fb";
const TABLE_HEAD_BG = "#0066CC";

export default function InvoiceDocument({ invoice, company, absolinLogoUrl }) {
  if (!invoice) return null;

  const isProforma = invoice.doc_type === "proforma";
  const title = isProforma ? "PROFORMA INVOICE" : "TAX INVOICE";
  const subTitle = isProforma ? "(Not a Tax Invoice — for advance / booking purposes)" : "(Original for Recipient)";

  const buyer = invoice.client_snapshot || {};
  const issuer = invoice.issuer_snapshot || {};
  const tax = invoice.tax || {};
  const items = invoice.line_items || [];
  const status = (invoice.status || "draft").toLowerCase();

  const hasCgstSgst = (tax.cgst_amt || 0) > 0 || (tax.sgst_amt || 0) > 0;
  const hasIgst = (tax.igst_amt || 0) > 0;
  const taxColSpan = hasCgstSgst ? 4 : hasIgst ? 2 : 0;

  const totalQty = items.reduce((s, li) => s + Number(li.qty || 0), 0);

  const logo = company?.logo_url || absolinLogoUrl;
  const signature = company?.signature_url || SIGN_URL;
  const stamp = company?.stamp_url || STAMP_URL;

  const companyName = issuer.name || company?.name || "Absolin Software Solutions LLP";
  const companyAddress = issuer.address || company?.address || "";
  const companyPhone = issuer.phone || company?.phone || "";
  const companyEmail = issuer.email || company?.email || "";
  const companyGstin = issuer.gstin || company?.gstin || "";
  const companyStateCode = issuer.state_code || company?.state_code || "";

  const bank = {
    name: company?.bank_account_name || companyName,
    ac: company?.bank_account_no || "",
    ifsc: company?.bank_ifsc || "",
    branch: company?.bank_branch || "",
    upi: company?.upi_id || "",
  };

  const defaultTerms = [
    "Payment is due within the credit period agreed upon acceptance of this invoice.",
    "Interest @ 18% p.a. will be charged on overdue amounts, at company's discretion.",
    "Subject to Visakhapatnam jurisdiction only.",
    "Services once rendered will not be refunded / adjusted, except as per the agreement.",
  ];
  const termsText = company?.terms_and_conditions || "";
  const terms = termsText
    ? termsText.split("\n").map(t => t.trim()).filter(Boolean)
    : defaultTerms;

  // Status ribbon (colour + label)
  const statusLabel = status === "paid" ? "PAID" : status === "cancelled" ? "CANCELLED" : status === "issued" ? "PENDING PAYMENT" : "DRAFT";
  const statusColor = status === "paid" ? "#059669" : status === "cancelled" ? "#dc2626" : status === "issued" ? BRAND : "#94a3b8";

  const cellBorder = `1px solid ${BORDER}`;

  // ─────────────────────────────────────────
  return (
    <div
      id="invoice-document"
      style={{
        width: "800px",
        padding: "24px",
        background: "#ffffff",
        color: "#0f172a",
        fontFamily: "'Helvetica Neue', 'Segoe UI', Arial, sans-serif",
        fontSize: "10.5px",
        lineHeight: 1.4,
        boxSizing: "border-box",
      }}
    >
      {/* Outer border wrapper */}
      <div style={{ border: `2px solid ${BRAND}`, borderRadius: 4 }}>
        {/* ================= HEADER ================= */}
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody>
            <tr>
              {/* LEFT — company block */}
              <td style={{ width: "58%", verticalAlign: "top", padding: "14px 16px", borderRight: cellBorder }}>
                {logo && (
                  <img src={logo} alt="Absolin" crossOrigin="anonymous"
                    style={{ height: 44, marginBottom: 6, display: "block" }} />
                )}
                <div style={{ fontSize: 18, fontWeight: 800, color: BRAND, letterSpacing: "0.3px", lineHeight: 1.15 }}>
                  {companyName.toUpperCase()}
                </div>
                <div style={{ fontSize: 10, color: "#475569", marginTop: 3, fontStyle: "italic" }}>
                  IT Solutions · ERP Implementation · AI Consulting
                </div>
                <div style={{ fontSize: 10, color: "#334155", marginTop: 6, whiteSpace: "pre-wrap", lineHeight: 1.45 }}>
                  {companyAddress}
                </div>
                <div style={{ fontSize: 10, color: "#334155", marginTop: 6 }}>
                  {companyPhone && <><strong>Tel:</strong> +91 {companyPhone}{"    "}</>}
                  {companyEmail && <><strong>Email:</strong> {companyEmail}</>}
                </div>
                <div style={{ fontSize: 10, color: "#0f172a", marginTop: 4 }}>
                  {companyGstin && <><strong>GSTIN:</strong> <span style={{ fontFamily: "'Courier New', monospace" }}>{companyGstin}</span>{"    "}</>}
                  {companyStateCode && <><strong>State Code:</strong> {companyStateCode}</>}
                </div>
              </td>

              {/* RIGHT — Title + meta */}
              <td style={{ width: "42%", verticalAlign: "top", padding: 0 }}>
                <div style={{
                  background: BRAND, color: "#fff", textAlign: "center",
                  padding: "12px 8px", letterSpacing: "3px",
                  fontSize: 18, fontWeight: 800,
                }}>
                  {title}
                </div>
                <div style={{ textAlign: "center", fontSize: 9, color: "#475569", padding: "4px 8px", borderBottom: cellBorder }}>
                  {subTitle}
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
                  <tbody>
                    <tr>
                      <td style={{ padding: "5px 10px", borderBottom: cellBorder, color: "#64748b", fontWeight: 600, width: "45%" }}>Invoice Number</td>
                      <td style={{ padding: "5px 10px", borderBottom: cellBorder, fontFamily: "'Courier New', monospace", fontWeight: 700, color: BRAND_DARK }}>
                        {invoice.invoice_no || "— (draft)"}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: "5px 10px", borderBottom: cellBorder, color: "#64748b", fontWeight: 600 }}>Invoice Date</td>
                      <td style={{ padding: "5px 10px", borderBottom: cellBorder }}>{fmtDate(invoice.issue_date)}</td>
                    </tr>
                    {invoice.due_date && (
                      <tr>
                        <td style={{ padding: "5px 10px", borderBottom: cellBorder, color: "#64748b", fontWeight: 600 }}>Due Date</td>
                        <td style={{ padding: "5px 10px", borderBottom: cellBorder }}>{fmtDate(invoice.due_date)}</td>
                      </tr>
                    )}
                    <tr>
                      <td style={{ padding: "5px 10px", borderBottom: cellBorder, color: "#64748b", fontWeight: 600 }}>Place of Supply</td>
                      <td style={{ padding: "5px 10px", borderBottom: cellBorder }}>
                        {buyer.client_state || "—"} {buyer.client_state_code ? `(${buyer.client_state_code})` : ""}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: "5px 10px", borderBottom: cellBorder, color: "#64748b", fontWeight: 600 }}>Reverse Charge</td>
                      <td style={{ padding: "5px 10px", borderBottom: cellBorder }}>No</td>
                    </tr>
                    <tr>
                      <td style={{ padding: "5px 10px", color: "#64748b", fontWeight: 600 }}>Category</td>
                      <td style={{ padding: "5px 10px" }}>{buyer.gst_category || "B2B"}</td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>
          </tbody>
        </table>

        {/* ================= CUSTOMER DETAIL (Bill-To) ================= */}
        <table style={{ width: "100%", borderCollapse: "collapse", borderTop: cellBorder }}>
          <tbody>
            <tr>
              <td style={{ padding: "6px 12px", background: HEAD_BG, borderBottom: cellBorder, fontSize: 11, fontWeight: 700, letterSpacing: "1.5px", color: BRAND_DARK }}>
                CUSTOMER DETAIL
              </td>
            </tr>
            <tr>
              <td style={{ padding: "10px 14px" }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: "#0f172a" }}>
                  M/s. {buyer.client_name || buyer.project_name || "—"}
                </div>
                {buyer.client_address && (
                  <div style={{ marginTop: 3, color: "#334155", whiteSpace: "pre-wrap", fontSize: 10 }}>{buyer.client_address}</div>
                )}
                <table style={{ marginTop: 6, fontSize: 10, borderCollapse: "collapse" }}>
                  <tbody>
                    {buyer.client_phone && (
                      <tr>
                        <td style={{ color: "#64748b", paddingRight: 12 }}>Ph No:</td>
                        <td style={{ fontWeight: 600 }}>{buyer.client_phone}</td>
                      </tr>
                    )}
                    {buyer.client_email && (
                      <tr>
                        <td style={{ color: "#64748b", paddingRight: 12 }}>Email:</td>
                        <td>{buyer.client_email}</td>
                      </tr>
                    )}
                    {buyer.client_gstin && (
                      <tr>
                        <td style={{ color: "#64748b", paddingRight: 12 }}>GSTIN:</td>
                        <td style={{ fontFamily: "'Courier New', monospace", fontWeight: 700 }}>{buyer.client_gstin}</td>
                      </tr>
                    )}
                    <tr>
                      <td style={{ color: "#64748b", paddingRight: 12 }}>Place of Supply:</td>
                      <td>{buyer.client_state || "—"} {buyer.client_state_code ? `(${buyer.client_state_code})` : ""}</td>
                    </tr>
                    {buyer.project_name && (
                      <tr>
                        <td style={{ color: "#64748b", paddingRight: 12 }}>Project:</td>
                        <td>{buyer.project_name}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </td>
            </tr>
          </tbody>
        </table>

        {/* ================= LINE ITEMS TABLE ================= */}
        <table style={{ width: "100%", borderCollapse: "collapse", borderTop: cellBorder }}>
          <thead>
            <tr style={{ background: TABLE_HEAD_BG, color: "#ffffff" }}>
              <th style={{ padding: "8px 4px", width: 32, fontSize: 10, borderRight: "1px solid rgba(255,255,255,0.25)" }}>Sr.<br />No.</th>
              <th style={{ padding: "8px 8px", textAlign: "left", fontSize: 10, borderRight: "1px solid rgba(255,255,255,0.25)" }}>Name of Service / Description</th>
              <th style={{ padding: "8px 4px", width: 62, fontSize: 10, borderRight: "1px solid rgba(255,255,255,0.25)" }}>HSN /<br />SAC</th>
              <th style={{ padding: "8px 4px", width: 42, fontSize: 10, borderRight: "1px solid rgba(255,255,255,0.25)" }}>Qty</th>
              <th style={{ padding: "8px 4px", width: 78, fontSize: 10, borderRight: "1px solid rgba(255,255,255,0.25)" }}>Rate</th>
              <th style={{ padding: "8px 4px", width: 90, fontSize: 10, borderRight: "1px solid rgba(255,255,255,0.25)" }}>Taxable<br />Value</th>
              {hasCgstSgst && (
                <>
                  <th colSpan={2} style={{ padding: "4px 0 0", fontSize: 10, borderRight: "1px solid rgba(255,255,255,0.25)" }}>
                    CGST
                    <div style={{ display: "flex", borderTop: "1px solid rgba(255,255,255,0.35)", marginTop: 3, fontSize: 9 }}>
                      <div style={{ flex: 1, padding: "3px 2px", borderRight: "1px solid rgba(255,255,255,0.25)" }}>%</div>
                      <div style={{ flex: 1.4, padding: "3px 2px" }}>Amount</div>
                    </div>
                  </th>
                  <th colSpan={2} style={{ padding: "4px 0 0", fontSize: 10, borderRight: "1px solid rgba(255,255,255,0.25)" }}>
                    SGST
                    <div style={{ display: "flex", borderTop: "1px solid rgba(255,255,255,0.35)", marginTop: 3, fontSize: 9 }}>
                      <div style={{ flex: 1, padding: "3px 2px", borderRight: "1px solid rgba(255,255,255,0.25)" }}>%</div>
                      <div style={{ flex: 1.4, padding: "3px 2px" }}>Amount</div>
                    </div>
                  </th>
                </>
              )}
              {hasIgst && (
                <th colSpan={2} style={{ padding: "4px 0 0", fontSize: 10, borderRight: "1px solid rgba(255,255,255,0.25)" }}>
                  IGST
                  <div style={{ display: "flex", borderTop: "1px solid rgba(255,255,255,0.35)", marginTop: 3, fontSize: 9 }}>
                    <div style={{ flex: 1, padding: "3px 2px", borderRight: "1px solid rgba(255,255,255,0.25)" }}>%</div>
                    <div style={{ flex: 1.4, padding: "3px 2px" }}>Amount</div>
                  </div>
                </th>
              )}
              <th style={{ padding: "8px 4px", width: 90, fontSize: 10 }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={7 + taxColSpan} style={{ padding: 20, textAlign: "center", color: "#94a3b8", fontStyle: "italic", border: cellBorder }}>No line items.</td></tr>
            ) : items.map((li, i) => {
              const taxable = Number(li.amount || 0);
              const cgstPct = hasCgstSgst ? Number(tax.cgst_pct || 0) : 0;
              const sgstPct = hasCgstSgst ? Number(tax.sgst_pct || 0) : 0;
              const igstPct = hasIgst ? Number(tax.igst_pct || 0) : 0;
              const cgstAmt = +(taxable * cgstPct / 100).toFixed(2);
              const sgstAmt = +(taxable * sgstPct / 100).toFixed(2);
              const igstAmt = +(taxable * igstPct / 100).toFixed(2);
              const rowTotal = +(taxable + cgstAmt + sgstAmt + igstAmt).toFixed(2);
              return (
                <tr key={i}>
                  <td style={{ padding: 6, border: cellBorder, textAlign: "center", verticalAlign: "top", color: "#64748b" }}>{i + 1}</td>
                  <td style={{ padding: 6, border: cellBorder, verticalAlign: "top", whiteSpace: "pre-wrap", fontWeight: 500 }}>{li.description || "—"}</td>
                  <td style={{ padding: 6, border: cellBorder, textAlign: "center", verticalAlign: "top", fontFamily: "'Courier New', monospace", color: "#475569" }}>{li.hsn_sac || "998313"}</td>
                  <td style={{ padding: 6, border: cellBorder, textAlign: "right", verticalAlign: "top", fontFamily: "'Courier New', monospace" }}>{Number(li.qty || 0).toFixed(2)}</td>
                  <td style={{ padding: 6, border: cellBorder, textAlign: "right", verticalAlign: "top", fontFamily: "'Courier New', monospace" }}>{fmt(li.rate)}</td>
                  <td style={{ padding: 6, border: cellBorder, textAlign: "right", verticalAlign: "top", fontFamily: "'Courier New', monospace" }}>{fmt(taxable)}</td>
                  {hasCgstSgst && (<>
                    <td style={{ padding: 6, border: cellBorder, textAlign: "center", verticalAlign: "top", fontSize: 9, color: "#64748b" }}>{cgstPct}%</td>
                    <td style={{ padding: 6, border: cellBorder, textAlign: "right", verticalAlign: "top", fontFamily: "'Courier New', monospace" }}>{fmt(cgstAmt)}</td>
                    <td style={{ padding: 6, border: cellBorder, textAlign: "center", verticalAlign: "top", fontSize: 9, color: "#64748b" }}>{sgstPct}%</td>
                    <td style={{ padding: 6, border: cellBorder, textAlign: "right", verticalAlign: "top", fontFamily: "'Courier New', monospace" }}>{fmt(sgstAmt)}</td>
                  </>)}
                  {hasIgst && (<>
                    <td style={{ padding: 6, border: cellBorder, textAlign: "center", verticalAlign: "top", fontSize: 9, color: "#64748b" }}>{igstPct}%</td>
                    <td style={{ padding: 6, border: cellBorder, textAlign: "right", verticalAlign: "top", fontFamily: "'Courier New', monospace" }}>{fmt(igstAmt)}</td>
                  </>)}
                  <td style={{ padding: 6, border: cellBorder, textAlign: "right", verticalAlign: "top", fontFamily: "'Courier New', monospace", fontWeight: 700 }}>{fmt(rowTotal)}</td>
                </tr>
              );
            })}
            {/* Totals row (mimicking reference: aggregate qty + taxable) */}
            <tr style={{ background: "#f8fafc", fontWeight: 700 }}>
              <td colSpan={3} style={{ padding: 6, border: cellBorder, textAlign: "right", color: "#0f172a" }}>Total</td>
              <td style={{ padding: 6, border: cellBorder, textAlign: "right", fontFamily: "'Courier New', monospace" }}>{totalQty.toFixed(2)}</td>
              <td style={{ padding: 6, border: cellBorder }}></td>
              <td style={{ padding: 6, border: cellBorder, textAlign: "right", fontFamily: "'Courier New', monospace" }}>{fmt(invoice.taxable_value)}</td>
              {hasCgstSgst && (<>
                <td style={{ padding: 6, border: cellBorder }}></td>
                <td style={{ padding: 6, border: cellBorder, textAlign: "right", fontFamily: "'Courier New', monospace" }}>{fmt(tax.cgst_amt)}</td>
                <td style={{ padding: 6, border: cellBorder }}></td>
                <td style={{ padding: 6, border: cellBorder, textAlign: "right", fontFamily: "'Courier New', monospace" }}>{fmt(tax.sgst_amt)}</td>
              </>)}
              {hasIgst && (<>
                <td style={{ padding: 6, border: cellBorder }}></td>
                <td style={{ padding: 6, border: cellBorder, textAlign: "right", fontFamily: "'Courier New', monospace" }}>{fmt(tax.igst_amt)}</td>
              </>)}
              <td style={{ padding: 6, border: cellBorder, textAlign: "right", fontFamily: "'Courier New', monospace", color: BRAND_DARK }}>{fmt(invoice.total)}</td>
            </tr>
          </tbody>
        </table>

        {/* ================= TOTAL IN WORDS + TAX SUMMARY ================= */}
        <table style={{ width: "100%", borderCollapse: "collapse", borderTop: cellBorder }}>
          <tbody>
            <tr>
              <td style={{ width: "58%", verticalAlign: "top", padding: "8px 12px", borderRight: cellBorder }}>
                <div style={{ fontSize: 9.5, color: "#64748b", fontWeight: 700, letterSpacing: 1 }}>TOTAL IN WORDS</div>
                <div style={{ fontSize: 11, color: "#0f172a", fontWeight: 700, fontStyle: "italic", marginTop: 2 }}>
                  {invoice.amount_in_words || "—"}
                </div>
                {invoice.narration && (
                  <>
                    <div style={{ fontSize: 9.5, color: "#64748b", fontWeight: 700, letterSpacing: 1, marginTop: 10 }}>NARRATION</div>
                    <div style={{ fontSize: 10, color: "#334155", marginTop: 2 }}>{invoice.narration}</div>
                  </>
                )}
              </td>
              <td style={{ width: "42%", verticalAlign: "top", padding: 0 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
                  <tbody>
                    <tr>
                      <td style={{ padding: "6px 12px", borderBottom: cellBorder, color: "#334155" }}>Taxable Amount</td>
                      <td style={{ padding: "6px 12px", borderBottom: cellBorder, textAlign: "right", fontFamily: "'Courier New', monospace", fontWeight: 700 }}>
                        ₹ {fmt(invoice.taxable_value)}
                      </td>
                    </tr>
                    {hasCgstSgst && (
                      <>
                        <tr>
                          <td style={{ padding: "5px 12px", borderBottom: cellBorder, color: "#334155" }}>Add: CGST ({tax.cgst_pct}%)</td>
                          <td style={{ padding: "5px 12px", borderBottom: cellBorder, textAlign: "right", fontFamily: "'Courier New', monospace" }}>₹ {fmt(tax.cgst_amt)}</td>
                        </tr>
                        <tr>
                          <td style={{ padding: "5px 12px", borderBottom: cellBorder, color: "#334155" }}>Add: SGST ({tax.sgst_pct}%)</td>
                          <td style={{ padding: "5px 12px", borderBottom: cellBorder, textAlign: "right", fontFamily: "'Courier New', monospace" }}>₹ {fmt(tax.sgst_amt)}</td>
                        </tr>
                      </>
                    )}
                    {hasIgst && (
                      <tr>
                        <td style={{ padding: "5px 12px", borderBottom: cellBorder, color: "#334155" }}>Add: IGST ({tax.igst_pct}%)</td>
                        <td style={{ padding: "5px 12px", borderBottom: cellBorder, textAlign: "right", fontFamily: "'Courier New', monospace" }}>₹ {fmt(tax.igst_amt)}</td>
                      </tr>
                    )}
                    <tr>
                      <td style={{ padding: "5px 12px", borderBottom: cellBorder, color: "#334155", fontWeight: 700 }}>Total Tax</td>
                      <td style={{ padding: "5px 12px", borderBottom: cellBorder, textAlign: "right", fontFamily: "'Courier New', monospace", fontWeight: 700 }}>
                        ₹ {fmt((tax.cgst_amt || 0) + (tax.sgst_amt || 0) + (tax.igst_amt || 0))}
                      </td>
                    </tr>
                    <tr style={{ background: BRAND, color: "#ffffff" }}>
                      <td style={{ padding: "10px 12px", fontWeight: 800, fontSize: 11.5, letterSpacing: 0.5 }}>Total Amount After Tax</td>
                      <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 800, fontSize: 13, fontFamily: "'Courier New', monospace" }}>
                        ₹ {fmt(invoice.total)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>
          </tbody>
        </table>

        {/* ================= BANK DETAILS + STATUS RIBBON ================= */}
        <table style={{ width: "100%", borderCollapse: "collapse", borderTop: cellBorder }}>
          <tbody>
            <tr>
              <td style={{ width: "58%", verticalAlign: "top", padding: "10px 12px", borderRight: cellBorder }}>
                <div style={{ fontSize: 9.5, color: "#64748b", fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>BANK DETAILS</div>
                <table style={{ fontSize: 10, borderCollapse: "collapse" }}>
                  <tbody>
                    <tr><td style={{ color: "#64748b", paddingRight: 12, paddingBottom: 2 }}>Beneficiary:</td><td style={{ fontWeight: 700, paddingBottom: 2 }}>{bank.name}</td></tr>
                    <tr><td style={{ color: "#64748b", paddingRight: 12, paddingBottom: 2 }}>Bank:</td><td style={{ paddingBottom: 2 }}>HDFC Bank Ltd.</td></tr>
                    <tr><td style={{ color: "#64748b", paddingRight: 12, paddingBottom: 2 }}>Branch:</td><td style={{ paddingBottom: 2 }}>{bank.branch || "—"}</td></tr>
                    <tr><td style={{ color: "#64748b", paddingRight: 12, paddingBottom: 2 }}>A/C Number:</td><td style={{ fontFamily: "'Courier New', monospace", fontWeight: 700, paddingBottom: 2 }}>{bank.ac || "—"}</td></tr>
                    <tr><td style={{ color: "#64748b", paddingRight: 12, paddingBottom: 2 }}>IFSC:</td><td style={{ fontFamily: "'Courier New', monospace", fontWeight: 700, paddingBottom: 2 }}>{bank.ifsc || "—"}</td></tr>
                    {bank.upi && (
                      <tr><td style={{ color: "#64748b", paddingRight: 12 }}>UPI ID:</td><td style={{ fontFamily: "'Courier New', monospace", fontWeight: 700, color: BRAND_DARK }}>{bank.upi}</td></tr>
                    )}
                  </tbody>
                </table>
              </td>
              <td style={{ width: "42%", verticalAlign: "middle", padding: "12px", textAlign: "center" }}>
                <div style={{
                  display: "inline-block", padding: "8px 22px",
                  border: `2px solid ${statusColor}`, color: statusColor,
                  fontSize: 14, fontWeight: 800, letterSpacing: 2, borderRadius: 4,
                  transform: "rotate(-1deg)",
                }}>
                  {statusLabel}
                </div>
                {status === "issued" && invoice.due_date && (
                  <div style={{ fontSize: 10, color: "#334155", marginTop: 8 }}>
                    Kindly remit by <strong>{fmtDate(invoice.due_date)}</strong><br />
                    quoting invoice <strong style={{ fontFamily: "'Courier New', monospace" }}>{invoice.invoice_no || "—"}</strong>
                  </div>
                )}
                {status === "paid" && (
                  <div style={{ fontSize: 10, color: "#065f46", marginTop: 8, fontWeight: 600 }}>
                    Payment received in full — Thank you for your business.
                  </div>
                )}
              </td>
            </tr>
          </tbody>
        </table>

        {/* ================= E-INVOICE (IRN + QR) — Tax invoices only ================= */}
        {invoice.doc_type === "tax" && (invoice.irn_number || invoice.qr_code_data) && (
          <table style={{ width: "100%", borderCollapse: "collapse", borderTop: cellBorder, background: "#f8fafc" }}>
            <tbody>
              <tr>
                <td style={{ width: "78%", verticalAlign: "middle", padding: "8px 12px", borderRight: cellBorder }}>
                  <div style={{ fontSize: 9.5, color: "#64748b", fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>
                    E-INVOICE DETAILS <span style={{ fontWeight: 500, letterSpacing: 0.5 }}>· Verified with GSTN IRP</span>
                  </div>
                  {invoice.irn_number && (
                    <>
                      <div style={{ fontSize: 9.5, color: "#475569", marginBottom: 2 }}>IRN (Invoice Reference Number)</div>
                      <div style={{
                        fontSize: 10, fontFamily: "'Courier New', monospace", fontWeight: 700,
                        color: "#0f172a", wordBreak: "break-all", lineHeight: 1.4,
                      }}>
                        {invoice.irn_number}
                      </div>
                    </>
                  )}
                  <div style={{ fontSize: 9, color: "#64748b", marginTop: 6, fontStyle: "italic" }}>
                    Digitally signed by GSTN Invoice Registration Portal.
                  </div>
                </td>
                <td style={{ width: "22%", verticalAlign: "middle", padding: "8px", textAlign: "center", background: "#ffffff" }}>
                  {invoice.qr_code_data ? (
                    <QRCodeSVG value={invoice.qr_code_data} size={100} level="M" includeMargin={false} />
                  ) : (
                    <div style={{ fontSize: 9, color: "#94a3b8", fontStyle: "italic", padding: 16 }}>
                      QR unavailable —<br />fetch signed<br />payload from IRP
                    </div>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        )}

        {/* ================= TERMS + SIGNATURE ================= */}
        <table style={{ width: "100%", borderCollapse: "collapse", borderTop: cellBorder }}>
          <tbody>
            <tr>
              <td style={{ width: "58%", verticalAlign: "top", padding: "10px 12px", borderRight: cellBorder }}>
                <div style={{ fontSize: 9.5, color: "#64748b", fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>TERMS &amp; CONDITIONS</div>
                <ol style={{ margin: 0, paddingLeft: 16, fontSize: 9.5, color: "#334155", lineHeight: 1.55 }}>
                  {terms.map((t, i) => (<li key={i} style={{ marginBottom: 2 }}>{t}</li>))}
                </ol>
              </td>
              <td style={{ width: "42%", verticalAlign: "top", padding: "10px 16px 12px", textAlign: "center" }}>
                <div style={{ fontSize: 9.5, color: "#64748b", fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>
                  Certified that the particulars given above are true &amp; correct
                </div>
                <div style={{ fontSize: 10.5, color: "#0f172a", fontWeight: 700, marginTop: 2, marginBottom: 8 }}>
                  For {companyName}
                </div>

                {/* DSC-styled signature panel (mimics Adobe Acrobat digital signature block) */}
                <div style={{
                  display: "inline-flex", alignItems: "stretch", gap: 0,
                  border: "1.2px solid #1d4ed8", borderRadius: 3,
                  background: "#eff6ff",
                  overflow: "hidden",
                  padding: 0,
                  minHeight: 70,
                }}>
                  {/* Left: signature + stamp side by side */}
                  <div style={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    padding: "6px 8px", borderRight: "1px solid #93c5fd",
                    background: "#ffffff",
                    minWidth: 130,
                  }}>
                    {signature && (
                      <img src={signature} alt="signature" crossOrigin="anonymous"
                        style={{ height: 58, mixBlendMode: "multiply" }} />
                    )}
                    {stamp && (
                      <img src={stamp} alt="stamp" crossOrigin="anonymous"
                        style={{
                          height: 58, width: 58,
                          marginLeft: -8,
                          mixBlendMode: "multiply",
                          opacity: 0.9,
                        }} />
                    )}
                  </div>
                  {/* Right: DSC info panel */}
                  <div style={{
                    padding: "6px 8px",
                    textAlign: "left",
                    fontSize: 7.5,
                    lineHeight: 1.35,
                    color: "#0f172a",
                    minWidth: 108,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}>
                    <div style={{ fontWeight: 700 }}>Digitally signed by</div>
                    <div style={{ fontWeight: 700, color: BRAND_DARK, marginTop: 1 }}>
                      {company?.dsc_signer_name || "Authorised Signatory"}
                    </div>
                    <div style={{ color: "#64748b", marginTop: 3 }}>
                      Date: {(() => {
                        const iso = invoice.issued_at || invoice.updated_at;
                        try {
                          const d = iso ? new Date(iso) : new Date();
                          return d.toISOString().slice(0, 19).replace("T", " ");
                        } catch { return new Date().toISOString().slice(0, 19).replace("T", " "); }
                      })()}
                    </div>
                    <div style={{ color: "#64748b" }}>Reason: Invoice approval</div>
                  </div>
                </div>

                <div style={{ borderTop: `1px solid ${BORDER_SOFT}`, paddingTop: 5, marginTop: 8, fontSize: 10.5, color: "#0f172a", fontWeight: 700 }}>
                  Authorised Signatory
                </div>
                <div style={{ fontSize: 9, color: "#64748b" }}>{companyName}</div>
              </td>
            </tr>
          </tbody>
        </table>

        {/* ================= FOOTER ================= */}
        <div style={{
          borderTop: cellBorder, padding: "8px 12px",
          fontSize: 9, color: "#0f172a", textAlign: "center",
          background: HEAD_BG, lineHeight: 1.55,
        }}>
          <div style={{ fontWeight: 700, color: BRAND_DARK, letterSpacing: 0.5 }}>
            Thank you for choosing Absolin — Your Partner in Digital Transformation.
          </div>
          <div style={{ fontStyle: "italic", color: "#475569", marginTop: 2 }}>
            {company?.footer_note || "This is a computer-generated invoice and does not require a physical signature."}
            {" · "}
            For any queries please write to <strong style={{ color: BRAND_DARK }}>{companyEmail || "info@absolin.com"}</strong>
            {" quoting invoice "}
            <strong style={{ fontFamily: "'Courier New', monospace" }}>{invoice.invoice_no || "(draft)"}</strong>.
          </div>
        </div>
      </div>
    </div>
  );
}
